import { Button, CommonModalProps, Container, TextBox } from "@animech/ui";
import {
	FunctionComponent,
	HTMLAttributes,
	KeyboardEvent,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import * as Styled from "./save-modal.style";
import { OpenIcon, useUiData } from "src/ui";
import { ModalBase } from "src/ui/components/modals/modal-base";
import { PrintButton } from "src/ui/components/widgets/print-button/print-button";
import { ApiDataContext, useSaveAndPdf } from "src/api";

type OpenDesignModalProps = CommonModalProps<HTMLAttributes<HTMLDivElement>>;

export const SaveModal: FunctionComponent<OpenDesignModalProps> = (
	baseProps,
) => {
	const {
		exportData: { name },
	} = useContext(ApiDataContext);

	const [activeForm, setActiveForm] = useState<"choose-action" | "save">(
		"choose-action",
	);
	const [newName, setNewName] = useState(name);

	const { localizer } = useUiData();
	const saveAndOpenPDf = useSaveAndPdf();

	const [isLoading, setIsLoading] = useState(false);

	const onSave = useCallback(async () => {
		if (!newName || isLoading) {
			return;
		}
		setIsLoading(true);
		await saveAndOpenPDf(newName);
		setIsLoading(false);
	}, [isLoading, newName, saveAndOpenPDf]);

	const onNameInputKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				onSave();
			}
		},
		[onSave],
	);

	const content = useMemo(() => {
		if (activeForm === "choose-action") {
			return (
				<Styled.Text>
					{localizer.localizeTerm("saveModalChooseAction")}
				</Styled.Text>
			);
		}

		return (
			<Container>
				<Styled.Text>
					{localizer.localizeTerm("saveModalEnterNameText")}
				</Styled.Text>
				<TextBox
					value={newName}
					onValueChange={setNewName}
					label={localizer.localizeTerm("saveModalEnterNameInputLabel")}
					instantValueChange
					inputAttributes={{ onKeyDown: onNameInputKeyDown }}
				/>
			</Container>
		);
	}, [activeForm, localizer, newName, onNameInputKeyDown]);

	const footerButtons = useMemo(() => {
		if (activeForm === "choose-action") {
			return [
				<PrintButton key="print-button" />,
				<Button key="save-as-button" onClick={() => setActiveForm("save")}>
					{localizer.localizeTerm("saveModalSaveAsButton")}
				</Button>,
			];
		}

		return [
			<Button
				disabled={!newName}
				key="save-button"
				onClick={onSave}
				busy={isLoading}
			>
				{localizer.localizeTerm("saveModalSaveButton")}
			</Button>,
		];
	}, [activeForm, isLoading, localizer, newName, onSave]);

	return (
		<ModalBase
			icon={<OpenIcon />}
			title={localizer.localizeTerm("saveModalTitle")}
			footerButtons={footerButtons}
			{...baseProps}
		>
			<Styled.SaveModalContent>{content}</Styled.SaveModalContent>
		</ModalBase>
	);
};
